import React, { useEffect, useMemo, useRef, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useForm } from 'react-hook-form';
import { IPrinterTemplate } from '../../../main/interfaces/IPrinterTemplate';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import useGetEnums from '../../../main/hooks/useGetEnums';
import '../Printers.css';
import { useTranslation } from 'react-i18next';

interface Props {
  template: IPrinterTemplate;
  onChange: (tpl: IPrinterTemplate) => void;
  onDelete?: (tpl: IPrinterTemplate) => void;
}

const DEFAULT_PRINTING_OPERATION = 1;

const EXAMPLE_HTML = `<p style="text-align:center; font-weight:700; margin:0;">{CompanyName}</p>
<p style="margin:6px 0 0 0;"><b>{InvoiceName}:</b> {InvoiceId}</p>
<p style="margin:2px 0 0 0;"><b>Table:</b> {Table}</p>
<p style="margin:2px 0 0 0;"><b>Time:</b> {Time}</p>
<p style="margin:2px 0 8px 0;"><b>Service:</b> {ServiceName}</p>

<hr style="border:none; border-top:1px solid #000; margin:8px 0;" />

<table style="width:100%; border-collapse:collapse; font-size:12px;">
  <tbody>
    {Items}
  </tbody>
</table>

{UmbrellaGroups}
{PlaygroundGroups}

<!-- FISCAL_START -->
<hr style="border:none; border-top:1px solid #000; margin:8px 0;" />
<p style="margin:0;"><b>NIVF:</b> {NIVF}</p>
<p style="margin:2px 0 0 0;"><b>NSLF:</b> {NSLF}</p>
<p style="margin:2px 0 0 0;"><b>Fiscal No:</b> {FiscalInvoiceNumber}</p>
<p style="margin:2px 0 0 0;"><b>Fiscal Time:</b> {FiscalDateTime}</p>
<p style="margin:2px 0 0 0;"><b>Business Code:</b> {BusinessCode}</p>
<p style="margin:2px 0 0 0;"><b>Operator Code:</b> {OperatorCode}</p>
<p style="margin:2px 0 0 0;"><b>Total EUR:</b> {TotalEur}</p>
<img src="{QRCode}" alt="QR code" style="width:96px; height:96px; margin-top:8px;" />
<!-- FISCAL_END -->

<hr style="border:none; border-top:1px solid #000; margin:8px 0;" />

<p style="margin:0;"><b>TOTAL:</b> {Total}</p>`;

const wrapForPreview = (raw: string) => {
  const s = (raw ?? '').trim();
  if (!s) return '';

  const hasHtmlTag = /<\s*html[\s>]/i.test(s);
  if (hasHtmlTag) return s;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }
  table { width: 100%; border-collapse: collapse; }
</style>
</head>
<body>
${s}
</body>
</html>`;
};

const normalizeTemplate = (template: IPrinterTemplate): IPrinterTemplate => ({
  ...template,
  operation: template.operation && template.operation > 0 ? template.operation : DEFAULT_PRINTING_OPERATION,
});

const PrinterTemplate = ({ template, onChange, onDelete }: Props) => {
  const allEnums = useGetEnums();
  const seededRef = useRef(false);
  const { i18n } = useTranslation();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const normalizedTemplate = useMemo(() => normalizeTemplate(template), [template]);

  const placeholderKeys = useMemo(
    () => [
      { key: '{CompanyName}', label: i18n.t('printers.keys.companyName', { defaultValue: 'Company name shown on the print.' }) },
      { key: '{InvoiceId}', label: i18n.t('printers.keys.invoiceId', { defaultValue: 'Invoice number or identifier.' }) },
      { key: '{InvoiceName}', label: i18n.t('printers.keys.invoiceName', { defaultValue: 'Table note for Bar/Restaurant prints; Invoice for other operations.' }) },
      { key: '{Table}', label: i18n.t('printers.keys.table', { defaultValue: 'Table name, code, or table id.' }) },
      { key: '{Time}', label: i18n.t('printers.keys.time', { defaultValue: 'Invoice creation time.' }) },
      { key: '{ServiceName}', label: i18n.t('printers.keys.serviceName', { defaultValue: 'Service or room label attached to the invoice.' }) },
      { key: '{BusinessAddress}', label: i18n.t('printers.keys.businessAddress', { defaultValue: 'Company business address.' }) },
      { key: '{Items}', label: i18n.t('printers.keys.items', { defaultValue: 'Rendered rows built by backend based on the selected operation.' }) },
      { key: '{UmbrellaGroups}', label: i18n.t('printers.keys.umbrellaGroups', { defaultValue: 'Price-group summary rendered for the Umbrella daily close operation.' }) },
      { key: '{PlaygroundGroups}', label: i18n.t('printers.keys.playgroundGroups', { defaultValue: 'Price-group summary rendered for the Playground daily close operation.' }) },
      { key: '{Total}', label: i18n.t('printers.keys.total', { defaultValue: 'Invoice total. For Albanian fiscal data this is the lek total.' }) },
      { key: '{TotalWithoutVAT}', label: i18n.t('printers.keys.totalWithoutVat', { defaultValue: 'Total before VAT.' }) },
      { key: '{FiscalInvoiceNumber}', label: i18n.t('printers.keys.fiscalInvoiceNumber', { defaultValue: 'Fiscal invoice number when fiscalized.' }) },
      { key: '{FiscalDateTime}', label: i18n.t('printers.keys.fiscalDateTime', { defaultValue: 'Fiscalization date and time.' }) },
      { key: '{BusinessCode}', label: i18n.t('printers.keys.businessCode', { defaultValue: 'Business code from fiscal data.' }) },
      { key: '{OperatorCode}', label: i18n.t('printers.keys.operatorCode', { defaultValue: 'Operator code used for fiscalization.' }) },
      { key: '{PaymentMethod}', label: i18n.t('printers.keys.paymentMethod', { defaultValue: 'Fiscal payment method.' }) },
      { key: '{ExchangeRate}', label: i18n.t('printers.keys.exchangeRate', { defaultValue: 'Exchange rate used by fiscalization.' }) },
      { key: '{Currency}', label: i18n.t('printers.keys.currency', { defaultValue: 'Fiscal currency code.' }) },
      { key: '{NIVF}', label: i18n.t('printers.keys.nivf', { defaultValue: 'Fiscal NIVF code.' }) },
      { key: '{NSLF}', label: i18n.t('printers.keys.nslf', { defaultValue: 'Fiscal NSLF code.' }) },
      { key: '{TotalEur}', label: i18n.t('printers.keys.totalEur', { defaultValue: 'Fiscal total in EUR.' }) },
      { key: '{QRCode}', label: i18n.t('printers.keys.qrCode', { defaultValue: 'Generated QR code image data.' }) },
      { key: '<!-- FISCAL_START --> ... <!-- FISCAL_END -->', label: i18n.t('printers.keys.fiscalBlock', { defaultValue: 'Optional block shown only when fiscal data exists.' }) },
    ],
    [i18n]
  );

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IPrinterTemplate>({ defaultValues: normalizedTemplate });

  useEffect(() => {
    reset(normalizedTemplate);
  }, [normalizedTemplate, reset]);

  useEffect(() => {
    const sub = watch((values) => onChange({ ...normalizedTemplate, ...values, operation: Number(values.operation) || DEFAULT_PRINTING_OPERATION }));
    return () => sub.unsubscribe();
  }, [watch, onChange, normalizedTemplate]);

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;

    const content = (normalizedTemplate as any)?.content ?? '';
    if (typeof content === 'string' && content.trim() === '') {
      setValue('content' as any, EXAMPLE_HTML as any, { shouldDirty: true });
    }
  }, [setValue, normalizedTemplate]);

  const htmlValue = (watch('content') as any) ?? '';
  const previewDoc = wrapForPreview(typeof htmlValue === 'string' && htmlValue.trim() ? htmlValue : EXAMPLE_HTML);

  return (
    <div className="template-card">
      <div className="template-header">
        <div className="template-header-main">
          <div className="template-input-wrap">
            <input
              type="text"
              className="form-input w-100"
              value={watch('name') ?? ''}
              onChange={(e) => setValue('name', e.target.value)}
              placeholder={i18n.t('printers.templateName')}
            />
          </div>

          <button
            type="button"
            className="template-info-btn"
            onClick={() => setIsInfoOpen((prev) => !prev)}
            aria-label={i18n.t('printers.templateKeysInfo', { defaultValue: 'Show available template keys' })}
            title={i18n.t('printers.templateKeysInfo', { defaultValue: 'Show available template keys' })}
          >
            <InfoOutlinedIcon fontSize="small" />
          </button>

          <div className="template-operation-wrap">
            <GeneralSelect
              label={i18n.t('entity.Operation')}
              name="operation"
              preSelectedVal={i18n.t('printers.selectOperation')}
              data={allEnums.PrintingOperation}
              value="name"
              translationTag="enum.printingOperation"
              required
              control={control}
              errors={errors}
            />
          </div>

          {onDelete && (
            <div className="template-actions">
              <button type="button" style={{ color: 'red' }} onClick={() => onDelete(normalizedTemplate)}>
                {i18n.t('generic.delete')}
              </button>
            </div>
          )}
        </div>
      </div>

     
      <div style={{ marginTop: '10px' }}>
        <div className="tplSplit">
          <div className="tplPane">
            <div className="tplPaneTitle">HTML</div>
            <textarea
              className="tplTextarea"
              value={htmlValue}
              onChange={(e) => setValue('content', e.target.value as any, { shouldDirty: true })}
              spellCheck={false}
              rows={18}
            />
          </div>

          <div className="tplPane">
            <div className="tplPaneTitle">{i18n.t('printers.preview')}</div>
            <iframe className="tplPreview" title="html-preview" srcDoc={previewDoc} sandbox="allow-same-origin" />
          </div>
        </div>
      </div>

       {isInfoOpen ? (
        <div className="template-info-popover">
          <div className="template-info-popover-header">
            <strong>{i18n.t('printers.availableTemplateKeys', { defaultValue: 'Available template keys' })}</strong>
            <span>{i18n.t('printers.availableTemplateKeysHint', { defaultValue: 'Use these placeholders in the HTML. Backend replaces them during printing.' })}</span>
          </div>
          <div className="template-info-list">
            {placeholderKeys.map((item) => (
              <div key={item.key} className="template-info-item">
                <code>{item.key}</code>
                <p>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

    </div>
  );
};

export default PrinterTemplate;
